// src/pages/FundPlanner/context/FundPlannerContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
} from "react";
import dayjs from "lib/dayjs";
import useCurrentOwner from "components/FileManager/hooks/useCurrentOwner";
import { normalizeSharing } from "lib/permissions";
import { useProfile } from "context/ProfileContext";

const FundPlannerContext = createContext();
export const useFundPlanner = () => useContext(FundPlannerContext);

const SESSION_KEY = "fundplanner_session_v1";

export function StructurerProvider({ children }) {
  const { selectedOrg } = useProfile();
  /* ---------------------------------------------
   * Dirty-state suppression
   * --------------------------------------------- */
  const suppressDirtyRef = useRef(false);

  /* ---------------------------------------------
   * Track whether session has been restored
   * --------------------------------------------- */
  const sessionRestoredRef = useRef(false);
  const [sessionReady, setSessionReady] = useState(false);

  /* ---------------------------------------------
   * Dirty flag
   * --------------------------------------------- */
  const [isDirty, setIsDirty] = useState(false);
  const markDirty = () => {
    if (!suppressDirtyRef.current) setIsDirty(true);
  };

  /* ---------------------------------------------
   * Planner settings
   * --------------------------------------------- */
  const [selectedCurve, _setSelectedCurve] = useState("slgs");
  const setSelectedCurve = (v) => {
    const next = v || "slgs";
    if (next !== selectedCurve && !suppressDirtyRef.current) {
      _setInvestmentSolution({});
      _setSufficiencyTable([]);
      _setCurveData([]);
      _setLastCurveUpdate(null);
    }
    _setSelectedCurve(next);
    markDirty();
  };

  const [fundMode, _setFundMode] = useState("gross");
  const setFundMode = (v) => {
    _setFundMode(v);
    markDirty();
  };

  const [fundType, _setFundType] = useState("project");
  const setFundType = (v) => {
    _setFundType(v);
    markDirty();
  };

  const [settlementDate, _setSettlementDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const setSettlementDate = (v) => {
    if (v !== settlementDate && !suppressDirtyRef.current) {
      _setInvestmentSolution({});
      _setSufficiencyTable([]);
    }
    _setSettlementDate(v);
    markDirty();
  };

  /* ---------------------------------------------
   * User inputs
   * --------------------------------------------- */
  const [asOfDate, _setAsOfDate] = useState("");
  const [totalAmount, _setTotalAmount] = useState(100_000_000);

  // track whether the user has explicitly edited Total Amount
  const [totalAmountTouched, _setTotalAmountTouched] = useState(false);

  /* ---------------------------------------------
   * Market curve
   * --------------------------------------------- */
  const [curveData, _setCurveData] = useState([]);
  const setCurveData = useCallback((v) => _setCurveData(v), []);

  const [customCurveRows, _setCustomCurveRows] = useState([]);
  const setCustomCurveRows = (v) => {
    _setCustomCurveRows(Array.isArray(v) ? v : []);
    if (!suppressDirtyRef.current) {
      _setInvestmentSolution({});
      _setSufficiencyTable([]);
    }
    markDirty();
  };
  const [userCurveDate, _setUserCurveDate] = useState("");
  const [userCurveDescription, _setUserCurveDescription] = useState("");
  const setUserCurveDate = (v) => {
    _setUserCurveDate(String(v || ""));
    if (!suppressDirtyRef.current) _setInvestmentSolution({});
    markDirty();
  };
  const setUserCurveDescription = (v) => {
    _setUserCurveDescription(String(v || ""));
    markDirty();
  };

  const [lastCurveUpdate, _setLastCurveUpdate] = useState(null);
  const setLastCurveUpdate = useCallback((v) => _setLastCurveUpdate(v), []);

  /* ---------------------------------------------
   * Results tables
   * --------------------------------------------- */
  const [drawSchedule, _setDrawSchedule] = useState([]);
  const setDrawSchedule = (v) => {
    _setDrawSchedule(v);
    markDirty();
  };

  const [investmentSolution, _setInvestmentSolution] = useState({});

  /**
   * Instrumented setter to identify unexpected clears.
   * - Logs when investments are empty or when caller passes {}
   * - Captures a stack trace for forensic tracing
   */
  const setInvestmentSolution = (v) => {
    _setInvestmentSolution(v);
  };

  const [sufficiencyTable, _setSufficiencyTable] = useState([]);
  const setSufficiencyTable = (v) => _setSufficiencyTable(v);

  /* ---------------------------------------------
   * Solver state
   * --------------------------------------------- */
  const [isSolving, setIsSolving] = useState(false);
  const [solverError, setSolverError] = useState(null);

  /* ---------------------------------------------
   * Workspace state
   * --------------------------------------------- */
  const [currentId, setCurrentId] = useState(null);
  const [currentName, setCurrentName] = useState("");
  const [createdBy, setCreatedBy] = useState(null);
  const currentOwner = useCurrentOwner(createdBy);
  const [activeTab, setActiveTab] = useState("Main");
  const [showWizard, setShowWizard] = useState(false);

  /* ---------------------------------------------
   * Setters that clear dependent outputs
   * --------------------------------------------- */

  /**
   * Clear solution any time market date changes (user-driven).
   *
   * IMPORTANT:
   * - During programmatic updates (load/reset/handoff/restore) we often hold
   *   suppressDirtyRef.current = true. In that mode, do NOT invalidate outputs
   *   because it can wipe loaded solutions due to ordering/batching.
   *
   * Instrumentation:
   * - Logs every change and whether invalidation ran
   * - Provides stack trace when invalidation is invoked
   */
  const setAsOfDate = (v) => {
    const next = v ?? "";

    if (asOfDate !== next && !suppressDirtyRef.current) {
      _setInvestmentSolution({});
      _setSufficiencyTable([]);
      _setCurveData([]);
      _setLastCurveUpdate(null);
    }
    _setAsOfDate(next);

    markDirty();
  };
  const [sharing, _setSharing] = useState("user");
  const setSharing = (v) => {
    _setSharing(normalizeSharing(v));
    markDirty();
  };

  // User-driven setter: marks dirty and marks touched
  const setTotalAmount = (v) => {
    _setTotalAmount(v);
    _setTotalAmountTouched(true);
    markDirty();
  };

  /**
   * Programmatic setter used for linkage: does NOT mark dirty.
   * Enhancement: allow force override to reset touched for linked/locked funds.
   */
  const setTotalAmountFromLink = (v, opts = {}) => {
    const { force = false } = opts || {};

    suppressDirtyRef.current = true;
    _setTotalAmount(v);

    // For linked/locked funds, we want DS to be the session truth.
    // Reset touched when forcing so downstream logic does not treat this as user-edited.
    if (force) _setTotalAmountTouched(false);

    suppressDirtyRef.current = false;
  };

  const setTotalAmountTouched = (v) => {
    _setTotalAmountTouched(Boolean(v));
  };

  const [fundName, _setFundName] = useState("");
  const setFundName = (v) => {
    _setFundName(v);
    markDirty();
  };

  const [linkedDealId, _setLinkedDealId] = useState(null);
  const setLinkedDealId = (v) => {
    _setLinkedDealId(v);
    markDirty();
  };

  /* ---------------------------------------------
   * Snapshot helpers
   * --------------------------------------------- */
  function getPlannerSnapshot() {
    return {
      orgId: selectedOrg?.id ?? null,
      fundType,
      sharing,
      fundMode,
      selectedCurve,
      settlementDate,

      asOfDate,
      totalAmount,
      totalAmountTouched,
      fundName,
      linkedDealId,

      // NOTE: curveData intentionally NOT persisted
      customCurveRows,
      userCurveDate,
      userCurveDescription,
      lastCurveUpdate,

      drawSchedule,
      investmentSolution,
      sufficiencyTable,

      isDirty,
      isSolving,
      solverError,

      currentId,
      currentName,
      createdBy,
      activeTab,
      showWizard,
    };
  }

  function loadPlannerSnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== "object") return;

    suppressDirtyRef.current = true;

    _setFundType(snapshot.fundType ?? "project");
    _setSharing(normalizeSharing(snapshot.sharing));
    _setFundMode(snapshot.fundMode ?? "gross");
    _setSelectedCurve(snapshot.selectedCurve ?? "slgs");
    _setSettlementDate(
      snapshot.settlementDate ?? dayjs().format("YYYY-MM-DD")
    );

    _setAsOfDate(snapshot.asOfDate ?? "");
    _setTotalAmount(
      typeof snapshot.totalAmount === "number"
        ? snapshot.totalAmount
        : 100_000_000
    );
    _setTotalAmountTouched(Boolean(snapshot.totalAmountTouched));

    _setFundName(snapshot.fundName ?? "");
    _setLinkedDealId(
      Object.prototype.hasOwnProperty.call(snapshot, "linkedDealId")
        ? snapshot.linkedDealId
        : null
    );

    // IMPORTANT: always start derived curve empty and refetch after restore
    _setCurveData([]);
    _setCustomCurveRows(
      Array.isArray(snapshot.customCurveRows) ? snapshot.customCurveRows : []
    );
    _setUserCurveDate(snapshot.userCurveDate ?? "");
    _setUserCurveDescription(snapshot.userCurveDescription ?? "");
    _setLastCurveUpdate(snapshot.lastCurveUpdate ?? null);

    _setDrawSchedule(
      Array.isArray(snapshot.drawSchedule) ? snapshot.drawSchedule : []
    );
    _setInvestmentSolution(
      snapshot.investmentSolution &&
        typeof snapshot.investmentSolution === "object"
        ? snapshot.investmentSolution
        : {}
    );
    _setSufficiencyTable(
      Array.isArray(snapshot.sufficiencyTable)
        ? snapshot.sufficiencyTable
        : []
    );

    setIsSolving(Boolean(snapshot.isSolving));
    setSolverError(
      Object.prototype.hasOwnProperty.call(snapshot, "solverError")
        ? snapshot.solverError
        : null
    );

    setIsDirty(Boolean(snapshot.isDirty));

    setCurrentId(
      Object.prototype.hasOwnProperty.call(snapshot, "currentId")
        ? snapshot.currentId
        : null
    );
    setCurrentName(snapshot.currentName ?? "");
    setCreatedBy(snapshot.createdBy ?? null);
    setActiveTab(snapshot.activeTab ?? "Main");
    setShowWizard(Boolean(snapshot.showWizard));

    suppressDirtyRef.current = false;
  }

  /* ---------------------------------------------
   * Restore from sessionStorage
   * --------------------------------------------- */
  useEffect(() => {
    if (sessionRestoredRef.current) return;
    sessionRestoredRef.current = true;

    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const snapshot = JSON.parse(raw);
        if (
          snapshot?.orgId &&
          selectedOrg?.id &&
          String(snapshot.orgId) === String(selectedOrg.id)
        ) {
          loadPlannerSnapshot(snapshot);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      }
    } catch (err) {
      console.error("FundPlanner session restore error:", err);
    } finally {
      setSessionReady(true);
    }
  }, [selectedOrg?.id]);

  /* ---------------------------------------------
   * Persist to sessionStorage
   * --------------------------------------------- */
  useEffect(() => {
    if (!sessionReady) return;

    try {
      const snapshot = getPlannerSnapshot();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(snapshot));
    } catch (err) {
      console.error("FundPlanner session persist error:", err);
    }
  }, [
    sessionReady,
    fundType,
    fundMode,
    selectedCurve,
    sharing,
    settlementDate,
    asOfDate,
    totalAmount,
    totalAmountTouched,
    fundName,
    linkedDealId,
    lastCurveUpdate,
    customCurveRows,
    userCurveDate,
    userCurveDescription,
    drawSchedule,
    investmentSolution,
    sufficiencyTable,
    isDirty,
    isSolving,
    solverError,
    currentId,
    currentName,
    createdBy,
    activeTab,
    showWizard,
    selectedOrg?.id,
  ]);

  /* ---------------------------------------------
   * Reset planner
   * --------------------------------------------- */
  const resetPlanner = () => {
    sessionStorage.removeItem(SESSION_KEY);
    suppressDirtyRef.current = true;

    _setFundType("project");
    _setSharing("user");
    _setFundMode("gross");
    _setSelectedCurve("slgs");
    _setSettlementDate(dayjs().format("YYYY-MM-DD"));

    _setAsOfDate("");
    _setTotalAmount(100_000_000);
    _setTotalAmountTouched(false);
    _setFundName("");
    _setLinkedDealId(null);

    _setCurveData([]);
    _setCustomCurveRows([]);
    _setUserCurveDate("");
    _setUserCurveDescription("");
    _setLastCurveUpdate(null);

    _setDrawSchedule([]);
    _setInvestmentSolution({});
    _setSufficiencyTable([]);

    setSolverError(null);
    setIsSolving(false);
    setIsDirty(false);

    setCurrentId(null);
    setCurrentName("");
    setCreatedBy(null);
    setActiveTab("Main");
    setShowWizard(false);

    setTimeout(() => {
      suppressDirtyRef.current = false;
    }, 0);
  };

  const previousOrgIdRef = useRef(selectedOrg?.id ?? null);
  useEffect(() => {
    const nextOrgId = selectedOrg?.id ?? null;
    const previousOrgId = previousOrgIdRef.current;
    previousOrgIdRef.current = nextOrgId;

    if (previousOrgId == null || String(previousOrgId) === String(nextOrgId)) return;
    resetPlanner();
  }, [selectedOrg?.id]);

  /* ---------------------------------------------
   * Render gate: do not mount children until restored
   * --------------------------------------------- */
  if (!sessionReady) return null;

  return (
    <FundPlannerContext.Provider
      value={{
        fundType,
        setFundType,
        sharing,
        setSharing,
        fundMode,
        setFundMode,
        selectedCurve,
        setSelectedCurve,
        settlementDate,
        setSettlementDate,

        asOfDate,
        setAsOfDate,
        totalAmount,
        setTotalAmount,
        totalAmountTouched,
        setTotalAmountTouched,
        setTotalAmountFromLink,

        fundName,
        setFundName,
        linkedDealId,
        setLinkedDealId,

        curveData,
        setCurveData,
        customCurveRows,
        setCustomCurveRows,
        userCurveDate,
        setUserCurveDate,
        userCurveDescription,
        setUserCurveDescription,
        lastCurveUpdate,
        setLastCurveUpdate,

        drawSchedule,
        setDrawSchedule,
        investmentSolution,
        setInvestmentSolution,
        sufficiencyTable,
        setSufficiencyTable,

        isSolving,
        setIsSolving,
        solverError,
        setSolverError,

        isDirty,
        setIsDirty,
        suppressDirtyRef,

        currentId,
        setCurrentId,
        currentName,
        setCurrentName,
        createdBy,
        setCreatedBy,
        currentOwner,
        activeTab,
        setActiveTab,
        showWizard,
        setShowWizard,

        resetPlanner,
      }}
    >
      {children}
    </FundPlannerContext.Provider>
  );
}
